import { useState } from "react";
import { toast } from "sonner";
import { Tool } from "lib/types";
import { getApiUrl, getStoredAuthData, storeAuthData } from "lib/utils";

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  remainingUpdates?: number;
  sessionToken?: string;
}

export interface UseApiState {
  loading: boolean;
  error: string | null;
}

export function useApi(tool: Tool) {
  const [state, setState] = useState<UseApiState>({
    loading: false,
    error: null,
  });

  const apiUrl = getApiUrl(tool);

  const makeRequest = async <T = unknown>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T> | null> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const storedAuth = getStoredAuthData(tool);
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(options.headers as Record<string, string> | undefined),
      };
      if (storedAuth?.sessionToken && !headers.Authorization) {
        headers.Authorization = `Bearer ${storedAuth.sessionToken}`;
      }

      const response = await fetch(`${apiUrl}${endpoint}`, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Request failed");
      }

      setState((prev) => ({ ...prev, loading: false }));
      return data;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An error occurred";
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      toast.error(errorMessage, { richColors: true });
      return null;
    }
  };

  return {
    ...state,
    makeRequest,
    apiUrl,
  };
}

export function useSubscriptionApi(tool: Tool) {
  const { makeRequest, loading, error } = useApi(tool);

  const sendOtp = async (email: string) => {
    const result = await makeRequest(
      `/generate-otp?email=${encodeURIComponent(email)}`,
    );
    if (result?.success) {
      toast.success(result.message || "OTP sent successfully!", {
        richColors: true,
      });
    }
    return result;
  };

  const getSubscriptions = async (email: string, otp: string) => {
    const result = await makeRequest(
      `/get-subscription?email=${encodeURIComponent(email)}&otp=${encodeURIComponent(otp)}`,
    );
    if (result?.success) {
      storeAuthData(tool, email, otp, result.sessionToken);
      toast.success("Subscriptions loaded successfully!", { richColors: true });
    }
    return result;
  };

  const updatePhoneNumber = async (
    subscriptionId: string,
    newNumber: string,
    email: string,
  ) => {
    const result = await makeRequest("/update-phone-number", {
      method: "POST",
      body: JSON.stringify({
        subscriptionId,
        newPhoneNumber: newNumber,
        email,
      }),
    });

    if (result?.success) {
      toast.success(
        result.message || "Telegram number updated successfully!",
        { richColors: true },
      );
    }
    return result;
  };

  const cancelSubscription = async (subscriptionId: string) => {
    const result = await makeRequest(
      `/cancel-subscription?subscriptionId=${subscriptionId}`,
    );

    if (result?.success) {
      toast.success(result.message || "Subscription cancelled successfully", {
        richColors: true,
      });
    }
    return result;
  };

  const reactivateSubscription = async (subscriptionId: string) => {
    const result = await makeRequest("/reactivate-subscription", {
      method: "POST",
      body: JSON.stringify({ subscriptionId }),
    });

    if (result?.success) {
      toast.success(result.message || "Subscription restored successfully", {
        richColors: true,
      });
    }
    return result;
  };

  return {
    loading,
    error,
    sendOtp,
    getSubscriptions,
    updatePhoneNumber,
    cancelSubscription,
    reactivateSubscription,
  };
}
